import { z } from 'zod';
import { isoDate, nonEmptyText, optionalText } from './common';

/**
 * Etapas de entrega: o(a) orientador(a) define o que o(a) orientando(a) precisa apresentar em cada
 * etapa (ex.: "10 artigos em PDF sobre o tema", "síntese da busca", "total de artigos triados").
 * `CHOICE` é uma lista suspensa de opções definidas pela orientação (ex.: fase atual da pesquisa).
 */
export const StepRequirementKind = { FILES: 'FILES', TEXT: 'TEXT', NUMBER: 'NUMBER', LINK: 'LINK', CHOICE: 'CHOICE' } as const;
export type StepRequirementKind = (typeof StepRequirementKind)[keyof typeof StepRequirementKind];
export const STEP_REQUIREMENT_LABELS: Record<StepRequirementKind, string> = {
  FILES: 'Arquivos',
  TEXT: 'Texto',
  NUMBER: 'Número',
  LINK: 'Link',
  CHOICE: 'Lista de opções',
};

const requirementId = z.string().regex(/^[a-z0-9-]{1,40}$/, 'Identificador de item inválido');

export const stepRequirementSchema = z
  .object({
    /** Ausente em itens novos (o servidor gera); preservado ao editar para manter as respostas. */
    id: requirementId.optional(),
    kind: z.enum(['FILES', 'TEXT', 'NUMBER', 'LINK', 'CHOICE']),
    label: nonEmptyText(200, 'Descrição do item'),
    description: optionalText(1000),
    required: z.boolean().default(true),
    /** Arquivos: quantidade mínima (ex.: 10 artigos). */
    minCount: z.number().int().min(1, 'Mínimo de 1 arquivo').max(500).optional(),
    /** Arquivos: extensões aceitas (vazio = as permitidas na plataforma). */
    extensions: z.array(z.string().trim().toLowerCase().regex(/^[a-z0-9]{1,10}$/, 'Extensão inválida')).max(20).optional(),
    /** Número: meta a atingir (ex.: 10). */
    target: z.number().min(0).max(1_000_000_000).optional(),
    unit: optionalText(40),
    /** Lista de opções: as escolhas possíveis, na ordem em que aparecem. */
    options: z.array(nonEmptyText(200, 'Texto da opção')).max(30, 'No máximo 30 opções').optional(),
  })
  .refine((r) => r.kind !== 'CHOICE' || new Set(r.options ?? []).size >= 2, { message: 'Informe ao menos duas opções', path: ['options'] })
  .transform((r) => ({
    ...r,
    minCount: r.kind === 'FILES' ? (r.minCount ?? 1) : undefined,
    extensions: r.kind === 'FILES' && r.extensions?.length ? [...new Set(r.extensions)] : undefined,
    target: r.kind === 'NUMBER' ? r.target : undefined,
    unit: r.kind === 'NUMBER' ? r.unit : undefined,
    options: r.kind === 'CHOICE' ? [...new Set(r.options)] : undefined,
  }));
export type StepRequirementInput = z.input<typeof stepRequirementSchema>;
/** `locked`: item obrigatório por regra (ex.: artigo submetido para a defesa) — não pode ser removido. */
export type StepRequirement = Omit<z.output<typeof stepRequirementSchema>, 'id'> & { id: string; locked?: boolean };

export const createStepSchema = z.object({
  title: nonEmptyText(200, 'Título'),
  description: optionalText(4000),
  dueDate: isoDate,
  requirements: z.array(stepRequirementSchema).max(30, 'No máximo 30 comprovações por atividade').default([]),
  /** Rótulo para agrupar (ex.: "Revisão sistemática", "Artigo 1") */
  group: optionalText(80),
  /** Atividade obrigatória (marcada pela orientação) */
  required: z.boolean().default(false),
});
export type CreateStepInput = z.input<typeof createStepSchema>;
export type CreateStepData = z.output<typeof createStepSchema>;

export const updateStepSchema = createStepSchema;

/** Confirmação de uma atividade sugerida pelo(a) orientando(a), com ajustes opcionais. */
export const confirmSuggestionSchema = z.object({ step: createStepSchema.optional() });
export type ConfirmSuggestionInput = z.infer<typeof confirmSuggestionSchema>;
/** Mesma atividade lançada de uma vez para vários(as) orientandos(as) (cada um recebe a sua). */
export const bulkActivitySchema = z.object({
  linkIds: z.array(z.uuid()).min(1, 'Escolha ao menos um(a) orientando(a)').max(200),
  step: createStepSchema,
});
export type BulkActivityInput = z.infer<typeof bulkActivitySchema>;
export const declineSuggestionSchema = z.object({ note: optionalText(2000) });
export type DeclineSuggestionInput = z.infer<typeof declineSuggestionSchema>;
export type UpdateStepInput = CreateStepInput;

/** Mover uma atividade no plano: para logo antes (`before`) ou logo depois (`after`) de outra. Só o prazo dela muda. */
export const moveStepSchema = z
  .object({ before: z.uuid().optional(), after: z.uuid().optional() })
  .refine((v) => Boolean(v.before) !== Boolean(v.after), { message: 'Informe a atividade de referência (antes ou depois)', path: ['before'] });
export type MoveStepInput = z.infer<typeof moveStepSchema>;

/** Respostas do(a) orientando(a) aos itens de texto, número, link e lista (por id do item; null limpa). */
export const saveStepAnswersSchema = z.object({
  answers: z.record(requirementId, z.union([z.string().trim().max(20_000), z.number().min(-1e12).max(1e12), z.null()])),
});
export type SaveStepAnswersInput = z.infer<typeof saveStepAnswersSchema>;

export const attachStepFilesSchema = z.object({
  requirementId,
  fileIds: z.array(z.uuid()).min(1).max(100),
});
export type AttachStepFilesInput = z.infer<typeof attachStepFilesSchema>;

export const reviewStepSchema = z
  .object({
    decision: z.enum(['APPROVE', 'REQUEST_CHANGES']),
    comment: optionalText(4000),
  })
  .refine((v) => v.decision === 'APPROVE' || !!v.comment, { message: 'Descreva os ajustes solicitados', path: ['comment'] });
export type ReviewStepInput = z.infer<typeof reviewStepSchema>;

/** Prorrogação/ajuste dos prazos do trabalho pela orientação (término/defesa e qualificação). */
export const extendWorkDeadlinesSchema = z
  .object({
    expectedEndDate: isoDate.optional(),
    qualificationDate: isoDate.optional(),
    justification: nonEmptyText(2000, 'Justificativa'),
  })
  .refine((v) => v.expectedEndDate || v.qualificationDate, { message: 'Informe ao menos uma nova data', path: ['expectedEndDate'] });
export type ExtendWorkDeadlinesInput = z.infer<typeof extendWorkDeadlinesSchema>;
